import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import ExpandMoreIcon   from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon  from '@mui/icons-material/HelpOutline';
import SearchIcon       from '@mui/icons-material/Search';
import PhoneIcon        from '@mui/icons-material/Phone';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SchoolIcon       from '@mui/icons-material/School';
import GroupsIcon       from '@mui/icons-material/Groups';
import LaptopIcon       from '@mui/icons-material/Laptop';
import BuildIcon        from '@mui/icons-material/Build';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const bounceY = keyframes`
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(7px); }
`;

// ─── Données FAQ avec catégories ─────────────────────────────────────────────
const FAQ_DATA = [
  {
    category: 'Inscription',
    icon: <SchoolIcon sx={{ fontSize: '1rem' }} />,
    items: [
      {
        question: 'Comment m\'inscrire à une formation ?',
        answer: (
          <>
            <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.8, mb: 1.5 }}>
              L'inscription se fait en quelques étapes simples :
            </Typography>
            <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 1, color: '#555', lineHeight: 1.7, fontSize: '0.9rem' } }}>
              <li>Allez à l'onglet <strong style={{ color: '#c62828' }}>Formations</strong> dans le menu</li>
              <li>Sélectionnez la formation qui vous intéresse</li>
              <li>Cliquez sur <strong style={{ color: '#c62828' }}>Voir les détails</strong></li>
              <li>Choisissez la date et cliquez sur <strong style={{ color: '#c62828' }}>S'inscrire</strong></li>
              <li>Remplissez le formulaire d'inscription</li>
              <li>Confirmez votre panier</li>
              <li>Réglez par <strong style={{ color: '#c62828' }}>carte bancaire</strong> sur la page sécurisée de paiement</li>
              <li>Vous recevez aussitôt votre confirmation et votre facture par courriel</li>
            </Box>
          </>
        ),
      },
      {
        question: 'Comment savoir si mon inscription a bien été confirmée ?',
        answer: (
          <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.85 }}>
            Une fois votre inscription complétée et votre paiement reçu, un <strong style={{ color: '#c62828' }}>courriel de confirmation</strong> sera envoyé à l'adresse fournie. Il contient la date, l'heure et les informations d'accès. Vérifiez vos courriels indésirables si vous ne le recevez pas.
          </Typography>
        ),
      },
      {
        question: 'J\'éprouve de la difficulté à m\'inscrire sur votre site, que dois-je faire ?',
        answer: (
          <>
            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.8, mb: 1.5 }}>
              Essayez d'abord ces solutions :
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 1.5, '& li': { mb: 0.75, color: '#555', fontSize: '0.9rem', lineHeight: 1.7 } }}>
              <li>Videz l'historique et le cache de votre navigateur</li>
              <li>Utilisez une fenêtre de navigation privée/incognito</li>
            </Box>
            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.8 }}>
              Si le problème persiste, contactez-nous à <strong style={{ color: '#c62828' }}>info@vitasecours.ca</strong> ou utilisez le bouton <strong style={{ color: '#c62828' }}>Nous joindre</strong>.
            </Typography>
          </>
        ),
      },
    ],
  },
  {
    category: 'Paiement et facturation',
    icon: <SchoolIcon sx={{ fontSize: '1rem' }} />,
    items: [
      {
        question: 'Quels moyens de paiement acceptez-vous ?',
        answer: (
          <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.85 }}>
            Deux moyens sont proposés, en dollars canadiens. Le <strong style={{ color: '#c62828' }}>paiement par carte</strong> se fait sur une page sécurisée de notre prestataire Stripe : votre numéro de carte est saisi chez lui et ne passe jamais par nos serveurs, et votre place est confirmée immédiatement. Vous pouvez sinon régler par <strong style={{ color: '#c62828' }}>virement Interac</strong> : votre inscription reste alors « en attente » jusqu'à réception du montant. La TPS et la TVQ sont affichées en détail avant toute validation.
          </Typography>
        ),
      },
      {
        question: 'Quand vais-je recevoir ma facture ?',
        answer: (
          <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.85 }}>
            Dès que le paiement est accepté. Vous recevez automatiquement un courriel de confirmation accompagné de votre <strong style={{ color: '#c62828' }}>facture au format PDF</strong>. Si rien n'arrive dans l'heure qui suit, vérifiez d'abord votre dossier de courriels indésirables.
          </Typography>
        ),
      },
      {
        question: 'Mon paiement a échoué. Ai-je perdu ma place et mes informations ?',
        answer: (
          <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.85 }}>
            Non. Aucun montant n'est prélevé et votre inscription n'est pas confirmée, mais les informations que vous aviez saisies sont conservées. Vous pouvez reprendre là où vous vous étiez arrêté, ou nous écrire à <strong style={{ color: '#c62828' }}>info@vitasecours.ca</strong> pour que nous finalisions avec vous. Attention : tant que le paiement n'est pas accepté, la place n'est pas garantie.
          </Typography>
        ),
      },
    ],
  },
  {
    category: 'Vos données personnelles',
    icon: <BuildIcon sx={{ fontSize: '1rem' }} />,
    items: [
      {
        question: 'Pourquoi demandez-vous les allergies et les besoins particuliers ?',
        answer: (
          <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.85 }}>
            Uniquement pour la sécurité du participant pendant la formation, et pour adapter le déroulement du cours si nécessaire. Ces champs sont <strong style={{ color: '#c62828' }}>facultatifs</strong> et ne sont consultables que par notre personnel. Ils ne sont jamais transmis à un tiers.
          </Typography>
        ),
      },
      {
        question: 'Mes informations sont-elles transmises à la Croix-Rouge ?',
        answer: (
          <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.85 }}>
            Oui, mais seulement ce qui est nécessaire à l'émission de votre certification : <strong style={{ color: '#c62828' }}>nom, prénom et courriel</strong>. C'est la raison pour laquelle chaque participant doit avoir sa propre adresse courriel. Rien d'autre n'est communiqué.
          </Typography>
        ),
      },
      {
        question: 'L\'assistant de discussion conserve-t-il ce que je lui écris ?',
        answer: (
          <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.85 }}>
            Votre message est transmis à un service d'intelligence artificielle pour produire la réponse, et l'historique de la conversation reste dans votre navigateur jusqu'à ce que vous la fermiez. <strong style={{ color: '#c62828' }}>N'y écrivez pas de renseignements personnels ou médicaux</strong> : pour cela, utilisez le formulaire d'inscription ou écrivez-nous directement.
          </Typography>
        ),
      },
      {
        question: 'Comment faire supprimer mes renseignements ?',
        answer: (
          <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.85 }}>
            Écrivez-nous à <strong style={{ color: '#c62828' }}>info@vitasecours.ca</strong>. Vous pouvez demander à consulter vos renseignements, à les faire corriger ou à les faire supprimer, sous réserve de ce que nous devons conserver pour vos certifications et nos obligations légales. Le détail figure dans notre politique de confidentialité.
          </Typography>
        ),
      },
    ],
  },
  {
    category: 'Formations pour jeunes',
    icon: <GroupsIcon sx={{ fontSize: '1rem' }} />,
    items: [
      {
        question: 'Quel est l\'âge minimum pour les formations Gardiens avertis et Prêts à rester seuls ?',
        answer: (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ p: 2, borderRadius: '12px', background: 'rgba(198,40,40,0.05)', borderLeft: '3px solid #c62828' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#c62828', mb: 0.5 }}>Gardiens avertis</Typography>
              <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7 }}>
                11 ans minimum ou 5e année du primaire complétée. Âge recommandé : jusqu'à 15 ans.
              </Typography>
            </Box>
            <Box sx={{ p: 2, borderRadius: '12px', background: 'rgba(198,40,40,0.05)', borderLeft: '3px solid #c62828' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#c62828', mb: 0.5 }}>Prêts à rester seuls</Typography>
              <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7 }}>
                9 ans minimum ou 3e année du primaire complétée. Âge recommandé : jusqu'à 13 ans.
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        question: 'Mes deux enfants peuvent-ils suivre le cours virtuel sur le même écran ?',
        answer: (
          <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.85 }}>
            Nous recommandons fortement que <strong style={{ color: '#c62828' }}>chaque participant ait son propre appareil</strong>. Cela permet à chaque enfant de participer pleinement à toutes les activités, d'interagir avec le formateur et d'appliquer correctement toutes les notions pratiques.
          </Typography>
        ),
      },
    ],
  },
  {
    category: 'Formations en ligne',
    icon: <LaptopIcon sx={{ fontSize: '1rem' }} />,
    items: [
      {
        question: 'Les formations virtuelles sont-elles aussi efficaces que les formations en présentiel ?',
        answer: (
          <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.85 }}>
            Absolument ! Le contenu enseigné est <strong style={{ color: '#c62828' }}>identique</strong> pour les deux formats. Nos formations virtuelles sont spécialement adaptées pour permettre d'apprendre les concepts, pratiquer les techniques et interagir avec le formateur. La flexibilité d'apprendre depuis chez soi est très appréciée des familles.
          </Typography>
        ),
      },
    ],
  },
  {
    category: 'Groupes & entreprises',
    icon: <GroupsIcon sx={{ fontSize: '1rem' }} />,
    items: [
      {
        question: 'Est-il possible d\'organiser une formation personnalisée pour mon entreprise ?',
        answer: (
          <>
            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.85, mb: 1.5 }}>
              Tout à fait ! L'un de nos formateurs peut se déplacer dans vos locaux pour proposer une formation adaptée à votre groupe d'employés.
            </Typography>
            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.85 }}>
              Envoyez-nous un courriel à <strong style={{ color: '#c62828' }}>info@vitasecours.ca</strong> en mentionnant le nombre de personnes, les dates envisagées et vos besoins spécifiques.
            </Typography>
          </>
        ),
      },
    ],
  },
  {
    category: 'Support technique',
    icon: <BuildIcon sx={{ fontSize: '1rem' }} />,
    items: [
      {
        question: 'Que faire en cas de problème technique pendant une formation ?',
        answer: (
          <>
            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.8, mb: 1.5 }}>
              Si vous rencontrez un problème technique :
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 1.5, '& li': { mb: 0.75, color: '#555', fontSize: '0.9rem', lineHeight: 1.7 } }}>
              <li>Essayez de vous reconnecter à la formation</li>
              <li>Vérifiez votre connexion Internet</li>
              <li>Redémarrez votre appareil si nécessaire</li>
            </Box>
            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.8 }}>
              Si le problème persiste, contactez-nous par courriel en décrivant la situation et en incluant les noms et courriels des participants affectés. Nous veillerons à ce que chacun reçoive son certificat de réussite.
            </Typography>
          </>
        ),
      },
    ],
  },
];

// ─── Styled ────────────────────────────────────────────────────────────────────
const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(150deg, rgba(183,28,28,0.97) 0%, rgba(90,0,0,0.93) 100%)',
  color: '#fff',
  padding: theme.spacing(14, 0, 12),
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -1, left: 0, right: 0,
    height: '70px',
    background: '#f7f8fc',
    clipPath: 'ellipse(60% 100% at 50% 100%)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-80px', right: '-80px',
    width: 340, height: 340,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.05)',
    pointerEvents: 'none',
  },
}));

const PageWrap = styled(Box)(({ theme }) => ({
  backgroundColor: '#f7f8fc',
  padding: theme.spacing(7, 0, 10),
}));

const Wrap = styled(Box)(({ theme }) => ({
  maxWidth: '860px',
  margin: '0 auto',
  padding: theme.spacing(0, 3),
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  background: '#ffffff',
  border: '1px solid rgba(0,0,0,0.07)',
  borderRadius: '16px !important',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  transition: 'all 0.25s ease',
  '&:hover': {
    boxShadow: '0 6px 20px rgba(198,40,40,0.1)',
    borderColor: 'rgba(198,40,40,0.2)',
  },
  '&.Mui-expanded': {
    borderColor: 'rgba(198,40,40,0.25)',
    boxShadow: '0 8px 28px rgba(198,40,40,0.12)',
  },
  '&:before': { display: 'none' },
}));

const StyledSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(0.5, 2.5),
  minHeight: 64,
  borderRadius: '16px',
  transition: 'all 0.2s ease',
  '& .MuiAccordionSummary-content': { margin: '14px 0 !important', alignItems: 'center', gap: theme.spacing(1.5) },
  '& .MuiAccordionSummary-expandIconWrapper': { color: '#c62828' },
  '&.Mui-expanded': { background: 'rgba(198,40,40,0.03)' },
}));

// ─── Composant ────────────────────────────────────────────────────────────────
const FAQ = () => {
  const [expanded, setExpanded] = useState('0-0');
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('Tous');

  const categories = ['Tous', ...FAQ_DATA.map(c => c.category)];

  // Filtrage par recherche et catégorie
  const filteredData = FAQ_DATA
    .filter(cat => activeCategory === 'Tous' || cat.category === activeCategory)
    .map(cat => ({
      ...cat,
      items: cat.items.filter(item =>
        item.question.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter(cat => cat.items.length > 0);

  const totalResults = filteredData.reduce((sum, cat) => sum + cat.items.length, 0);

  return (
    <>
      {/* ══════════════════ HERO ══════════════════ */}
      <HeroSection>
        <Box sx={{ position: 'absolute', bottom: '80px', left: '-50px', width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
        <Wrap sx={{ position: 'relative', zIndex: 2, maxWidth: '700px' }}>
          <Box sx={{ animation: `${fadeUp} 0.8s ease both` }}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 72, height: 72,
              borderRadius: '20px',
              border: '2px solid rgba(255,255,255,0.3)',
              background: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(4px)',
              mb: 3,
              color: '#fff',
            }}>
              <HelpOutlineIcon sx={{ fontSize: 34 }} />
            </Box>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.2rem', md: '3.4rem' }, fontWeight: 900, mb: 2.5, lineHeight: 1.1 }}>
              Questions fréquentes
            </Typography>
            <Typography sx={{ maxWidth: '560px', mx: 'auto', fontSize: '1.1rem', lineHeight: 1.85, opacity: 0.88 }}>
              Retrouvez les réponses aux questions les plus courantes sur nos formations et notre processus d'inscription.
            </Typography>
          </Box>
          <Box sx={{ mt: 5, animation: `${bounceY} 2.2s ease infinite` }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 28, opacity: 0.5 }} />
          </Box>
        </Wrap>
      </HeroSection>

      {/* ══════════════════ CORPS ══════════════════ */}
      <PageWrap>
        <Wrap>

          {/* Barre de recherche */}
          <Box sx={{ mb: 4 }}>
            <TextField
              fullWidth
              placeholder="Rechercher une question…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#c62828' }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '50px',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
                  '& fieldset': { borderColor: 'transparent' },
                  '&:hover fieldset': { borderColor: 'rgba(198,40,40,0.3)' },
                  '&.Mui-focused fieldset': { borderColor: '#c62828' },
                  pl: 0.5,
                },
              }}
            />
          </Box>

          {/* Filtres par catégorie */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 4 }}>
            {categories.map(cat => (
              <Chip
                key={cat}
                label={cat}
                onClick={() => setActiveCategory(cat)}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  backgroundColor: activeCategory === cat ? '#c62828' : '#fff',
                  color: activeCategory === cat ? '#fff' : '#555',
                  border: `1.5px solid ${activeCategory === cat ? '#c62828' : 'rgba(0,0,0,0.1)'}`,
                  boxShadow: activeCategory === cat ? '0 4px 12px rgba(198,40,40,0.25)' : 'none',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: activeCategory === cat ? '#b71c1c' : 'rgba(198,40,40,0.06)' },
                }}
              />
            ))}
            {(search || activeCategory !== 'Tous') && (
              <Typography variant="caption" sx={{ color: '#aaa', alignSelf: 'center', ml: 1 }}>
                {totalResults} résultat{totalResults !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>

          {/* Accordéons par catégorie */}
          {filteredData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: '#aaa' }}>
              <HelpOutlineIcon sx={{ fontSize: 48, mb: 2, opacity: 0.4 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Aucun résultat</Typography>
              <Typography variant="body2">Essayez un autre terme ou contactez-nous directement.</Typography>
            </Box>
          ) : (
            filteredData.map((cat, catIdx) => (
              <Box key={cat.category} sx={{ mb: 4 }}>
                {/* Titre de catégorie */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 32, height: 32,
                    borderRadius: '8px',
                    background: 'rgba(198,40,40,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#c62828',
                  }}>
                    {cat.icon}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '0.95rem' }}>
                    {cat.category}
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', background: 'rgba(0,0,0,0.07)' }} />
                  <Typography variant="caption" sx={{ color: '#bbb', flexShrink: 0 }}>
                    {cat.items.length} question{cat.items.length > 1 ? 's' : ''}
                  </Typography>
                </Box>

                {cat.items.map((item, itemIdx) => {
                  const key = `${catIdx}-${itemIdx}`;
                  return (
                    <StyledAccordion
                      key={key}
                      expanded={expanded === key}
                      onChange={(_, isExpanded) => setExpanded(isExpanded ? key : false)}
                    >
                      <StyledSummary expandIcon={<ExpandMoreIcon />}>
                        {/* Numéro */}
                        <Box sx={{
                          width: 28, height: 28, borderRadius: '8px',
                          background: expanded === key ? '#c62828' : 'rgba(198,40,40,0.1)',
                          color: expanded === key ? '#fff' : '#c62828',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.72rem', fontWeight: 800, flexShrink: 0,
                          transition: 'all 0.2s',
                        }}>
                          {itemIdx + 1}
                        </Box>
                        <Typography sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '0.97rem', lineHeight: 1.4 }}>
                          {item.question}
                        </Typography>
                      </StyledSummary>
                      <AccordionDetails sx={{
                        px: 3, pb: 3, pt: 0.5,
                        borderTop: '1px solid rgba(0,0,0,0.05)',
                        ml: '52px', // aligné avec le texte de la question
                      }}>
                        {item.answer}
                      </AccordionDetails>
                    </StyledAccordion>
                  );
                })}
              </Box>
            ))
          )}

          {/* ── CTA bas de page ── */}
          <Box sx={{
            mt: 6,
            p: { xs: 4, md: 5 },
            borderRadius: '24px',
            background: 'linear-gradient(135deg, #c62828, #8e0000)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""', position: 'absolute',
              top: '-50px', right: '-50px',
              width: 200, height: 200, borderRadius: '50%',
              background: 'rgba(255,255,255,0.06)', pointerEvents: 'none',
            },
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#fff', mb: 1.5 }}>
                Vous n'avez pas trouvé votre réponse ?
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', mb: 3.5, maxWidth: 500, mx: 'auto' }}>
                Notre équipe est disponible pour répondre à toutes vos questions personnalisées.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button
                  component={Link} to="/contact"
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: '#fff', color: '#c62828', fontWeight: 800,
                    px: 4, py: 1.5, borderRadius: '50px',
                    '&:hover': { background: 'rgba(255,255,255,0.92)' },
                  }}
                >
                  Nous contacter
                </Button>
                <Button
                  href="tel:4503005005"
                  variant="outlined"
                  startIcon={<PhoneIcon />}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.6)', color: '#fff', fontWeight: 700,
                    px: 4, py: 1.5, borderRadius: '50px', borderWidth: 2,
                    '&:hover': { borderWidth: 2, borderColor: '#fff', background: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  450 300-5005
                </Button>
              </Stack>
            </Box>
          </Box>

        </Wrap>
      </PageWrap>
    </>
  );
};

export default FAQ;
